import { Skeleton, View } from "@/components/ui";
import { Typography } from "@/components/shared/typography";
import { CaretRight } from "@/components/shared/icons";
import { BottomDrawerModal } from "@/components/shared/bottom-drawer-modal";
import { useState } from "react";
import { Divider } from "@/components/shared/divider";
import { formatPrice } from "@/lib/helpers/number";
import { formatDateTime } from "@/lib/helpers/date";
import { addDays, addMonths } from "date-fns";
import {
  CreditAccountTransactionSortByEnum,
  CreditAccountTransactionStatusEnum,
  CreditTypeEnum,
  SortEnum,
} from "@/__generated__/graphql";
import { useCreditAccountTransactionByFiltersQuery } from "@/gqlhooks/profile/useCreditAccountTransactionByFiltersQuery";
import { CreditAccountType } from "@/types/profile";

const now = new Date();
const expirationDateFrom = addDays(now, 1).toISOString();
const expirationDateTo = addMonths(now, 6).toISOString();

const departmentCreditConfigByType = {
  massage: { linkedName: "MASSAGE", label: "Massage" },
  beauty: { linkedName: "HOME_BEAUTY", label: "Beauty" },
  pet: { linkedName: "PET_CARE", label: "Pet Care" },
} as const;

export const CreditAccountExpiryList = ({
  type,
}: {
  type: Extract<CreditAccountType, "massage" | "beauty" | "pet">;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { linkedName, label } = departmentCreditConfigByType[type];
  const { list, loading } = useCreditAccountTransactionByFiltersQuery(
    {
      sortBy: [CreditAccountTransactionSortByEnum.ExpirationDate],
      sort: [SortEnum.Asc],
      expirationDateFrom,
      expirationDateTo,
      status: [CreditAccountTransactionStatusEnum.Authorized],
    },
    CreditTypeEnum.Department,
    linkedName,
  );

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      setIsOpen(false);
    }
  };

  const hasExpiredTransactions = list.filter(
    (t) => t.expirationDate != null && t.remainingAmount !== 0,
  );

  const nearestExpirationDate =
    hasExpiredTransactions.find((t) => t.expirationDate)?.expirationDate ??
    null;

  if (loading) {
    return <Skeleton className="h-5 w-full" />;
  }

  if (!nearestExpirationDate) {
    return null;
  }
  const totalAmount = hasExpiredTransactions
    .filter((t) => t.expirationDate === nearestExpirationDate)
    .reduce((acc, t) => acc + Number(t.remainingAmount), 0);

  return (
    <>
      <View
        className="flex cursor-pointer flex-row"
        onClick={() => setIsOpen(true)}
      >
        <Typography variant="body-sm" color="foreground-intermediate">
          {formatPrice(totalAmount)} {label} Credit expiring by{" "}
          {formatDateTime(new Date(nearestExpirationDate), "MMM d, yyyy")}
        </Typography>
        <CaretRight size={16} />
      </View>
      <BottomDrawerModal open={isOpen} onOpenChange={handleClose}>
        <View className="flex flex-col">
          <View className="mb-5 flex flex-col gap-2 pb-4">
            <Typography variant="label-sm">Credits by expiry</Typography>
            <Typography variant="body-sm" color="foreground-intermediate">
              Credits are used in the order they are purchased. This page shows
              the credits that are expiring soon, within 6 months.
            </Typography>
          </View>
          <View className="flex flex-col gap-3">
            <Divider />
            {hasExpiredTransactions.map((transaction, index) => {
              const expirationDate = transaction.expirationDate
                ? formatDateTime(
                    new Date(transaction.expirationDate),
                    "MMM d, yyyy",
                  )
                : "N/A";
              return (
                <>
                  <View
                    className="flex flex-row items-center justify-between"
                    key={transaction.id}
                  >
                    <Typography variant="label-sm" color="brand-primary">
                      {formatPrice(transaction.remainingAmount ?? 0)}
                    </Typography>
                    <Typography variant="caption-md">
                      Expiring by {expirationDate}
                    </Typography>
                  </View>
                  {index !== hasExpiredTransactions.length - 1 && <Divider />}
                </>
              );
            })}
          </View>
        </View>
      </BottomDrawerModal>
    </>
  );
};
